import { FC } from 'react';
import { NavLink } from 'react-router-dom';
import {
  alpha,
  buttonClasses,
  css,
  Drawer,
  styled,
  Theme,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';

import { Button, ButtonProps, ToolbarOffset } from '@components/base';

const useNavMenuColor = (): ButtonProps['color'] => {
  const theme = useTheme();
  return theme.palette.mode === 'dark' ? 'secondary' : 'primary';
};

export const Nav = styled('nav')`
  padding: ${({ theme }) => theme.spacing()};

  ul {
    list-style-type: none;
    padding: 0;
  }
`;

const getMenuItemCSS = (theme: Theme) => css`
  text-transform: none;
  width: 100%;
  justify-content: flex-start;

  &.active {
    background: ${theme.palette.background.paper};
  }

  &:focus-visible {
    outline: 2px solid ${theme.palette.text.primary};
    outline-offset: -2px;
  }
`;

export const RootLink = styled(
  ({ disableRipple = true, leftIcon, color, ...props }: ButtonProps) => {
    const navMenuColor = useNavMenuColor();

    return (
      <Button
        {...props}
        color={color ?? navMenuColor}
        component={NavLink}
        disableRipple={disableRipple}
        leftIcon={leftIcon}
      />
    );
  }
)`
  ${({ theme }) => getMenuItemCSS(theme)}
`;

export const RootButton = styled(({ color, disableRipple = true, ...props }: ButtonProps) => {
  const navMenuColor = useNavMenuColor();
  return <Button color={color ?? navMenuColor} disableRipple={disableRipple} {...props} />;
})`
  ${({ theme }) => getMenuItemCSS(theme)}

  .${buttonClasses.startIcon} {
    transition: ${({ theme }) => theme.transitions.duration.short}ms;
  }

  &.expanded .${buttonClasses.startIcon} {
    rotate: 90deg;
  }
`;

export const RootGroupWrapper = styled('li')`
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;

  &.expanded {
    background: ${({ theme }) =>
      alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.3 : 0.7)};
  }

  &:hover {
    background: ${({ theme }) => theme.palette.background.paper};
  }
`;

export const SublistHeading = styled('li')`
  padding-left: ${({ theme }) => theme.spacing(3)};
  margin: ${({ theme }) => theme.spacing(1, 0, 0.5, 0)};
  color: ${({ theme }) => theme.palette.text.secondary};
  position: relative;

  &:after {
    background: ${({ theme }) => theme.palette.text.secondary};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    content: '';
    display: block;
    height: 8px;
    left: 8px;
    opacity: 0.3;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
  }
`;

export const SublistItem = styled(
  ({ disableRipple = true, leftIcon, color, ...props }: ButtonProps) => {
    const navMenuColor = useNavMenuColor();

    return (
      <Button
        {...props}
        color={color ?? navMenuColor}
        component={NavLink}
        disableRipple={disableRipple}
        leftIcon={leftIcon}
      />
    );
  }
)`
  ${({ theme }) => getMenuItemCSS(theme)}

  padding-left: ${({ theme }) => theme.spacing(4)};
  padding-top: ${({ theme }) => theme.spacing(0.75)};
  padding-bottom: ${({ theme }) => theme.spacing(0.75)};
  position: relative;

  .${buttonClasses.startIcon} {
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  &:after {
    position: absolute;
    top: 0;
    height: 100%;
    left: ${({ theme }) => theme.spacing(1.4)};
    content: '';
    width: 1.5px;
    background: ${({ theme }) => theme.palette.divider};
  }

  &:hover,
  &:focus-visible {
    .btn-label {
      color: ${({ theme }) => theme.palette.text.primary};
    }

    .${buttonClasses.startIcon} {
      color: ${({ theme }) => theme.palette.text.primary};
    }

    &:not(.active):after {
      opacity: 0.5;
      background: ${({ theme }) => theme.palette.text.secondary};
    }
  }

  &.active {
    .btn-label {
      color: currentColor;
    }

    &:after {
      background: currentColor;
    }

    .${buttonClasses.startIcon} {
      color: currentColor;
    }

    &:hover {
      background: ${({ theme }) => alpha(theme.palette.primary.main, 0.15)};
    }
  }

  &:not(.active) {
    font-weight: normal !important;
  }
`;

export const RootButtonText: FC<TypographyProps> = ({
  color = 'text.primary',
  variant = 'h5',
  component = 'span',
  ...rest
}) => <Typography color={color} component={component} variant={variant} {...rest} />;

export const drawerWidth = 240;

export const StyledDrawer = styled(Drawer)`
  transition: width 0.2s;
  width: ${drawerWidth}px;
  white-space: nowrap;
  overflow-x: hidden;

  > div {
    z-index: ${props => props.theme.zIndex.appBar - 1};
    overflow-x: hidden;
    transition: width 0.2s;
    width: ${drawerWidth}px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    &,
    > div {
      border: none;
      width: ${props => (props.open ? '100vw' : 0)};
    }
  }
`;

export const DrawerHeader = styled(ToolbarOffset)`
  align-items: center;
  display: flex;
  padding: ${({ theme }) => theme.spacing(0, 1)};
`;

export const ActionButton = styled(
  ({ leftIcon, color = 'inherit', fullWidth = true, ...props }: ButtonProps) => (
    <Button {...props} color={color} fullWidth={fullWidth} leftIcon={leftIcon} />
  )
)`
  ${({ theme }) => getMenuItemCSS(theme)}
`;
